import React, { useState, useEffect, useMemo } from 'react';
import { getOrders, deleteOrder, deductOrderStock, getBillitConfig, sendBillitDeliveryNote } from '../../services/api';
import { Plus, Trash2, Search, Package, PackageCheck, Receipt, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import Pagination from '../../components/Pagination';
import OrderForm from './OrderForm';
import OrderDetail from './OrderDetail';

export default function OrdersTab({ autoNew, onAutoNewHandled }) {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState('list');
    const [editingOrder, setEditingOrder] = useState(null);
    const [viewingOrder, setViewingOrder] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterCustomer, setFilterCustomer] = useState('');
    const [filterDateRange, setFilterDateRange] = useState('');
    const [filterDateFrom, setFilterDateFrom] = useState('');
    const [filterDateTo, setFilterDateTo] = useState('');
    const [selectedIds, setSelectedIds] = useState([]);
    const [page, setPage] = useState(1);
    const [billitConfig, setBillitConfig] = useState(null);
    const [billitSendingId, setBillitSendingId] = useState(null);
    const PAGE_SIZE = 20;

    useEffect(() => { fetchOrders(); getBillitConfig().then(r => setBillitConfig(r.data)).catch(() => {}); }, []);
    useEffect(() => { if (autoNew) { openCreate(); if (onAutoNewHandled) onAutoNewHandled(); } }, [autoNew]);
    const fetchOrders = async () => {
        try { setOrders((await getOrders()).data); }
        catch { toast.error('Siparisler yuklenemedi'); }
        finally { setLoading(false); }
    };

    const openCreate = () => { setEditingOrder(null); setView('form'); };
    const openEdit = (order) => { setEditingOrder(order); setView('form'); };
    const openCopy = (order) => { setEditingOrder({ ...order, id: null, order_number: null, status: 'saved', _copy: true }); setView('form'); };
    const openView = (order) => { setViewingOrder(order); setView('view'); };
    const closeForm = () => { setView('list'); setEditingOrder(null); setViewingOrder(null); };
    const handleSaved = () => { closeForm(); fetchOrders(); };

    const handleDelete = async (id) => {
        if (!window.confirm('Siparisi silmek?')) return;
        try { await deleteOrder(id); toast.success('Siparis silindi'); fetchOrders(); }
        catch { toast.error('Silinemedi'); }
    };

    const handleBulkDelete = async () => {
        if (!selectedIds.length) return;
        if (!window.confirm(`${selectedIds.length} siparis silinecek?`)) return;
        try {
            await Promise.all(selectedIds.map(id => deleteOrder(id)));
            toast.success(`${selectedIds.length} siparis silindi`);
            setSelectedIds([]);
            fetchOrders();
        } catch { toast.error('Toplu silme hatasi'); }
    };

    const toggleSelect = (id) => setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    const toggleAll = () => {
        const pageIds = paged.map(o => o.id);
        if (pageIds.every(id => selectedIds.includes(id))) setSelectedIds(prev => prev.filter(id => !pageIds.includes(id)));
        else setSelectedIds(prev => [...new Set([...prev, ...pageIds])]);
    };

    const handleDeductStock = async (order) => {
        if (order.stock_deducted) { toast.info('Stok zaten dusuldu'); return; }
        if (!window.confirm(`${order.order_number} siparisinin stoklarini dusmek istediginize emin misiniz?`)) return;
        try {
            await deductOrderStock(order.id);
            toast.success('Stoktan basariyla dusuldu');
            fetchOrders();
        } catch { toast.error('Stok dusme hatasi'); }
    };

    const handleBillitSend = async (order) => {
        if (order.billit_order_id || billitSendingId) return;
        if (!window.confirm(`${order.order_number} siparisini Billit'e gondermek istediginize emin misiniz?`)) return;
        setBillitSendingId(order.id);
        try {
            const res = await sendBillitDeliveryNote(order.id);
            toast.success(res.data.message || 'Billit\'e gonderildi');
            fetchOrders();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Billit gonderim hatasi');
        } finally {
            setBillitSendingId(null);
        }
    };

    const uniqueCustomers = useMemo(() => [...new Set(orders.map(o => o.customer_name).filter(Boolean))].sort(), [orders]);

    const filtered = useMemo(() => {
        let res = orders;
        if (searchTerm) {
            const q = searchTerm.toLowerCase();
            res = res.filter(o => o.order_number?.toLowerCase().includes(q) || o.customer_name?.toLowerCase().includes(q));
        }
        if (filterCustomer) res = res.filter(o => o.customer_name === filterCustomer);
        if (filterDateFrom) res = res.filter(o => o.order_date >= filterDateFrom);
        if (filterDateTo) res = res.filter(o => o.order_date <= filterDateTo);
        return res;
    }, [orders, searchTerm, filterCustomer, filterDateFrom, filterDateTo]);

    const paged = useMemo(() => filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE), [filtered, page]);
    const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
    const fmt = (v) => Math.round(v || 0).toLocaleString('de-DE');

    if (view === 'form') return <OrderForm onSaved={handleSaved} onCancel={closeForm} editOrder={editingOrder} />;
    if (view === 'view') return <OrderDetail order={viewingOrder} onBack={closeForm} onRefresh={fetchOrders} onEdit={openEdit} onCopy={openCopy} />;

    return (
        <div className="space-y-4 max-w-6xl">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <p className="text-body">{filtered.length} siparis{filtered.length !== orders.length ? ` / ${orders.length} toplam` : ''}</p>
                <div className="flex items-center gap-2">
                    {selectedIds.length > 0 && (
                        <button onClick={handleBulkDelete} className="h-9 px-3 rounded-lg bg-[#FEF2F2] text-[#EF4444] text-sm font-medium flex items-center gap-1.5 hover:bg-[#FEE2E2] transition-colors" data-testid="bulk-delete-orders">
                            <Trash2 size={14} /> {selectedIds.length} sil
                        </button>
                    )}
                    <button onClick={openCreate} className="btn btn-primary" data-testid="add-order-btn">
                        <Plus size={16} /> Yeni Siparis
                    </button>
                </div>
            </div>

            {orders.length > 0 && (
                <div className="flex flex-wrap items-center gap-3">
                    <div className="relative flex-1 min-w-[200px] max-w-sm">
                        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input type="text" placeholder="Siparis no veya musteri..." value={searchTerm} onChange={e => { setSearchTerm(e.target.value); setPage(1); }} className="input-apple pl-9 text-sm h-9" data-testid="order-search-input" />
                    </div>
                    <select value={filterCustomer} onChange={e => { setFilterCustomer(e.target.value); setPage(1); }} className="input-apple text-sm h-9 w-auto" data-testid="order-filter-customer">
                        <option value="">Tum Musteriler</option>
                        {uniqueCustomers.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <select value={filterDateRange} onChange={e => {
                        const val = e.target.value;
                        setFilterDateRange(val);
                        setPage(1);
                        const today = new Date();
                        const fmtDate = d => d.toISOString().split('T')[0];
                        if (val === 'today') { setFilterDateFrom(fmtDate(today)); setFilterDateTo(fmtDate(today)); }
                        else if (val === 'week') { const d = new Date(today); d.setDate(d.getDate() - 7); setFilterDateFrom(fmtDate(d)); setFilterDateTo(fmtDate(today)); }
                        else if (val === 'month') { const d = new Date(today); d.setDate(d.getDate() - 30); setFilterDateFrom(fmtDate(d)); setFilterDateTo(fmtDate(today)); }
                        else if (val === 'thismonth') { const d = new Date(today.getFullYear(), today.getMonth(), 1); setFilterDateFrom(fmtDate(d)); setFilterDateTo(fmtDate(today)); }
                        else { setFilterDateFrom(''); setFilterDateTo(''); }
                    }} className="input-apple text-sm h-9 w-auto" data-testid="order-filter-date-range">
                        <option value="">Tum Tarihler</option>
                        <option value="today">Bugun</option>
                        <option value="week">Son 7 Gun</option>
                        <option value="thismonth">Bu Ay</option>
                        <option value="month">Son 30 Gun</option>
                        <option value="custom">Ozel Aralik</option>
                    </select>
                    {filterDateRange === 'custom' && (
                        <>
                            <input type="date" value={filterDateFrom} onChange={e => { setFilterDateFrom(e.target.value); setPage(1); }} className="input-apple text-sm h-9 w-auto" title="Baslangic" data-testid="order-filter-from" />
                            <input type="date" value={filterDateTo} onChange={e => { setFilterDateTo(e.target.value); setPage(1); }} className="input-apple text-sm h-9 w-auto" title="Bitis" data-testid="order-filter-to" />
                        </>
                    )}
                    {(filterCustomer || filterDateFrom || filterDateTo) && (
                        <button onClick={() => { setFilterCustomer(''); setFilterDateFrom(''); setFilterDateTo(''); setPage(1); }} className="text-xs text-[#6B7280] hover:text-[#EF4444] transition-colors">Temizle</button>
                    )}
                </div>
            )}

            {loading ? <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="skeleton h-14 rounded-xl" />)}</div>
            : filtered.length > 0 ? (
                <div className="bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden overflow-x-auto">
                    <table className="w-full mobile-cards" data-testid="orders-table">
                        <colgroup>
                            <col className="w-10" />
                            <col className="w-[120px]" />
                            <col />
                            <col className="w-[110px]" />
                            <col className="w-[100px]" />
                            <col className="w-[130px]" />
                        </colgroup>
                        <thead>
                            <tr className="border-b border-[#E5E7EB]">
                                <th className="px-3 py-3"><input type="checkbox" checked={paged.length > 0 && paged.every(o => selectedIds.includes(o.id))} onChange={toggleAll} className="rounded" data-testid="select-all-orders" /></th>
                                <th className="text-left px-3 py-3 text-[11px] font-semibold text-[#6B7280] uppercase tracking-wider">Siparis No</th>
                                <th className="text-left px-3 py-3 text-[11px] font-semibold text-[#6B7280] uppercase tracking-wider">Musteri</th>
                                <th className="text-left px-3 py-3 text-[11px] font-semibold text-[#6B7280] uppercase tracking-wider mobile-hide">Tarih</th>
                                <th className="text-right px-3 py-3 text-[11px] font-semibold text-[#6B7280] uppercase tracking-wider">Toplam</th>
                                <th className="text-right px-3 py-3 text-[11px] font-semibold text-[#6B7280] uppercase tracking-wider mobile-hide">Islem</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paged.map(o => (
                                <tr key={o.id} className={`border-b border-[#F3F4F6] hover:bg-[#FAFAFA] transition-colors cursor-pointer ${selectedIds.includes(o.id) ? 'bg-[#EFF6FF]' : ''}`} data-testid={`order-row-${o.id}`} onClick={(e) => { if (e.target.type !== 'checkbox' && !e.target.closest('button')) openView(o); }}>
                                    <td className="px-3 py-2.5 mobile-hide" onClick={e => e.stopPropagation()}><input type="checkbox" checked={selectedIds.includes(o.id)} onChange={() => toggleSelect(o.id)} className="rounded" data-testid={`select-order-${o.id}`} /></td>
                                    <td className="px-3 py-2.5"><span className="font-mono font-semibold text-[#3B82F6] text-sm" data-testid={`view-order-${o.id}`}>{o.order_number}</span></td>
                                    <td className="px-3 py-2.5 text-sm font-medium text-[#111827] truncate">{o.customer_name}</td>
                                    <td className="px-3 py-2.5 text-sm text-[#6B7280] mobile-hide">{new Date(o.order_date).toLocaleDateString('tr-TR')}</td>
                                    <td className="px-3 py-2.5 text-right font-semibold text-sm text-[#111827]">&euro;{fmt(o.total)}</td>
                                    <td className="px-3 py-2.5 text-right mobile-hide" onClick={e => e.stopPropagation()}>
                                        <div className="flex items-center justify-end gap-0.5">
                                            <button onClick={() => handleDeductStock(o)} className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${o.stock_deducted ? 'bg-[#ECFDF5] cursor-default' : 'hover:bg-[#EFF6FF]'}`} title={o.stock_deducted ? 'Stok dusuldu' : 'Stoktan dus'} disabled={o.stock_deducted} data-testid={`deduct-stock-${o.id}`}>
                                                <PackageCheck size={14} className={o.stock_deducted ? 'text-[#059669]' : 'text-[#6B7280]'} />
                                            </button>
                                            {billitConfig?.api_key && (
                                                <button onClick={() => handleBillitSend(o)} className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${o.billit_order_id ? 'bg-[#FFF7ED] cursor-default' : 'hover:bg-[#FFF7ED]'}`} title={o.billit_order_id ? `Billit #${o.billit_order_id}` : 'Billit\'e Gonder'} disabled={o.billit_order_id || billitSendingId === o.id} data-testid={`billit-send-${o.id}`}>
                                                    {billitSendingId === o.id ? <Loader2 size={14} className="text-[#F97316] animate-spin" /> : <Receipt size={14} className={o.billit_order_id ? 'text-[#F97316]' : 'text-[#6B7280]'} />}
                                                </button>
                                            )}
                                            <button onClick={() => handleDelete(o.id)} className="w-7 h-7 rounded-lg hover:bg-[#FEF2F2] flex items-center justify-center transition-colors" title="Sil" data-testid={`delete-order-${o.id}`}><Trash2 size={14} className="text-[#EF4444]" /></button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {totalPages > 1 && <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />}
                </div>
            ) : (
                <div className="bg-white rounded-2xl border border-[#E5E7EB] text-center py-16">
                    <Package className="w-14 h-14 text-[#D1D5DB] mx-auto mb-3" />
                    <h3 className="text-base font-semibold text-[#111827] mb-1">Henuz siparis yok</h3>
                    <p className="text-sm text-[#9CA3AF] mb-5">Siparis olusturmak icin baslayin</p>
                    <button onClick={openCreate} className="btn btn-primary"><Plus size={16} /> Yeni Siparis</button>
                </div>
            )}
        </div>
    );
}
